/**
 * Socket.IO Connection Manager for Admin Portal
 *
 * Manages real-time WebSocket connections using Socket.IO:
 * - Auto-reconnect with exponential backoff
 * - Connection state management
 * - Event subscription system
 * - Message queueing for offline mode
 * - Authentication with JWT
 * - Admin-specific namespaces (admin, payments, dashboard, audit)
 *
 * @see WICL-DOC-002-D API Integration Patterns
 */

import { io, Socket } from "socket.io-client";

/** Bounded reconnection so a persistent auth/network failure can't loop forever. */
const MAX_RECONNECTION_ATTEMPTS = 20;

/**
 * Connection state enum
 */
export enum ConnectionState {
  Connected = "Connected",
  Connecting = "Connecting",
  Disconnected = "Disconnected",
  Reconnecting = "Reconnecting",
}

/**
 * Socket.IO event types for admin portal
 */
export enum SocketIOEvent {
  // Payment events (most critical for admin)
  PaymentVerified = "payment:verified",
  PaymentRejected = "payment:rejected",
  PaymentRefunded = "payment:refunded",
  PaymentPending = "payment:pending",

  // Resource events
  ResourceStatusChanged = "resource:status",
  ResourceProvisioned = "resource:provisioned",
  ResourceTerminated = "resource:terminated",

  // Notification events
  NotificationReceived = "notification:received",

  // Dashboard events
  MetricsUpdate = "metrics:update",
  DashboardAlert = "dashboard:alert",

  // Audit events
  AuditLog = "audit:log",
}

/**
 * Event handler type
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO events carry arbitrary payloads
export type EventHandler<T = any> = (data: T) => void;

/**
 * Event subscription
 */
interface EventSubscription {
  event: string;
  handler: EventHandler;
  namespace: string;
}

/**
 * Payment event data
 */
export interface PaymentEvent {
  paymentId: string;
  tenantId: string;
  amount: number;
  status: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Dashboard metrics event data
 */
export interface MetricsEvent {
  type: string;
  value: number;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Audit log event data
 */
export interface AuditLogEvent {
  id: string;
  action: string;
  userId: string;
  resourceType: string;
  resourceId: string;
  timestamp: string;
  details?: Record<string, unknown>;
}

/**
 * Resolve Socket.IO base URL with the following priority:
 * 1. Runtime config from Kubernetes ConfigMap (window.APP_CONFIG.socketUrl)
 * 2. Build-time environment variable (VITE_SOCKET_URL)
 * 3. Empty string — relative URL, same origin as the page
 */
const getSocketUrl = (): string => {
  if (typeof window !== "undefined" && window.APP_CONFIG?.socketUrl) {
    return window.APP_CONFIG.socketUrl;
  }
  return import.meta.env.VITE_SOCKET_URL || "";
};

/**
 * Get token from localStorage
 */
const getTokenFromStorage = (): string => {
  try {
    const authData = localStorage.getItem("wicloud_admin_auth");
    if (authData) {
      const { accessToken } = JSON.parse(authData);
      return accessToken || "";
    }
  } catch (error) {
    console.error("[Socket.IO Admin] Failed to get access token:", error);
  }
  return "";
};

/**
 * Determine namespace for an event
 */
const getNamespaceForEvent = (event: string): string => {
  // Payment events go to /payments namespace
  if (event.startsWith("payment:")) {
    return "/payments";
  }
  // Dashboard events go to /dashboard namespace
  if (event.startsWith("metrics:") || event.startsWith("dashboard:")) {
    return "/dashboard";
  }
  // Audit events go to /audit namespace
  if (event.startsWith("audit:")) {
    return "/audit";
  }
  // All other events go to /admin namespace
  return "/admin";
};

/**
 * Socket.IO Connection Manager Class for Admin Portal
 */
class SocketIOConnectionManager {
  private adminSocket: Socket | null = null;
  private paymentsSocket: Socket | null = null;
  private dashboardSocket: Socket | null = null;
  private auditSocket: Socket | null = null;
  private connectionState: ConnectionState = ConnectionState.Disconnected;
  private subscriptions: Map<string, EventSubscription[]> = new Map();
  private messageQueue: Array<{
    event: string;
    data: unknown[];
    namespace: string;
  }> = [];
  private reconnectAttempts = 0;
  private reconnectDelays = [1000, 2000, 4000, 8000, 16000, 30000]; // Exponential backoff
  private stateChangeListeners: Array<(state: ConnectionState) => void> = [];
  private baseUrl: string;

  constructor() {
    this.baseUrl = getSocketUrl();
  }

  /**
   * Initialize Socket.IO connections
   */
  async initialize(accessToken?: string): Promise<void> {
    if (this.adminSocket || this.paymentsSocket) {
      console.log("[Socket.IO Admin] Connections already initialized");
      return;
    }

    const token = accessToken || getTokenFromStorage();

    if (!token) {
      console.warn("[Socket.IO Admin] No access token available");
      return;
    }

    console.log("[Socket.IO Admin] Initializing connections to:", this.baseUrl);

    // Connect to admin namespace (for general admin events)
    await this.connectToNamespace("/admin", token);

    // Connect to payments namespace (for payment verification events)
    await this.connectToNamespace("/payments", token);

    // Connect to dashboard namespace (for real-time metrics)
    await this.connectToNamespace("/dashboard", token);

    // Connect to audit namespace (for audit log streaming)
    await this.connectToNamespace("/audit", token);
  }

  /**
   * Connect to a specific namespace
   */
  private async connectToNamespace(
    namespace: string,
    token: string,
  ): Promise<void> {
    const socket = io(`${this.baseUrl}${namespace}`, {
      // Dynamic auth: read the freshest token on every (re)connect so a refreshed
      // JWT is picked up instead of looping forever on an expired one.
      auth: (cb) => cb({ token: getTokenFromStorage() || token }),
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: this.reconnectDelays[0],
      reconnectionDelayMax:
        this.reconnectDelays[this.reconnectDelays.length - 1],
      // Bounded so a persistent auth/network failure can't loop forever.
      reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
    });

    // Connection lifecycle handlers
    socket.on("connect", () => {
      console.log(`[Socket.IO Admin] Connected to ${namespace}`, socket.id);
      this.updateConnectionState(ConnectionState.Connected);
      this.reconnectAttempts = 0;
      this.flushMessageQueue();
    });

    socket.on("disconnect", (reason: string) => {
      console.log(`[Socket.IO Admin] Disconnected from ${namespace}:`, reason);
      if (reason === "io server disconnect") {
        // Server disconnected, reconnect manually
        socket.connect();
      } else {
        this.updateConnectionState(ConnectionState.Disconnected);
      }
    });

    socket.on("connect_error", (error: Error) => {
      console.error(
        `[Socket.IO Admin] Connection error on ${namespace}:`,
        error,
      );
      this.updateConnectionState(ConnectionState.Reconnecting);
      this.reconnectAttempts++;
    });

    socket.on("reconnect", (attemptNumber: number) => {
      console.log(
        `[Socket.IO Admin] Reconnected to ${namespace} after ${attemptNumber} attempts`,
      );
      this.updateConnectionState(ConnectionState.Connected);
      this.reconnectAttempts = 0;
      this.flushMessageQueue();
    });

    socket.on("reconnect_attempt", (attemptNumber: number) => {
      console.log(
        `[Socket.IO Admin] Reconnect attempt ${attemptNumber} to ${namespace}`,
      );
      this.updateConnectionState(ConnectionState.Reconnecting);
    });

    socket.on("reconnect_error", (error: Error) => {
      console.error(
        `[Socket.IO Admin] Reconnect error on ${namespace}:`,
        error,
      );
    });

    socket.on("reconnect_failed", () => {
      console.error(`[Socket.IO Admin] Reconnect failed on ${namespace}`);
      this.updateConnectionState(ConnectionState.Disconnected);
    });

    // Store socket reference
    if (namespace === "/admin") {
      this.adminSocket = socket;
    } else if (namespace === "/payments") {
      this.paymentsSocket = socket;
    } else if (namespace === "/dashboard") {
      this.dashboardSocket = socket;
    } else if (namespace === "/audit") {
      this.auditSocket = socket;
    }
  }

  /**
   * Connect to Socket.IO namespaces
   */
  async connect(): Promise<void> {
    const token = getTokenFromStorage();
    if (!token) {
      console.error("[Socket.IO Admin] No access token available");
      return;
    }

    if (this.connectionState === ConnectionState.Connected) {
      console.log("[Socket.IO Admin] Already connected");
      return;
    }

    try {
      this.updateConnectionState(ConnectionState.Connecting);
      await this.initialize(token);
    } catch (error) {
      console.error("[Socket.IO Admin] Connection failed:", error);
      this.updateConnectionState(ConnectionState.Disconnected);
      throw error;
    }
  }

  /**
   * Disconnect from all namespaces
   */
  async disconnect(): Promise<void> {
    try {
      if (this.adminSocket) {
        this.adminSocket.disconnect();
        this.adminSocket = null;
      }
      if (this.paymentsSocket) {
        this.paymentsSocket.disconnect();
        this.paymentsSocket = null;
      }
      if (this.dashboardSocket) {
        this.dashboardSocket.disconnect();
        this.dashboardSocket = null;
      }
      if (this.auditSocket) {
        this.auditSocket.disconnect();
        this.auditSocket = null;
      }
      this.updateConnectionState(ConnectionState.Disconnected);
      console.log("[Socket.IO Admin] Disconnected from all namespaces");
    } catch (error) {
      console.error("[Socket.IO Admin] Disconnect failed:", error);
    }
  }

  /**
   * Get socket for a specific namespace
   */
  private getSocketForNamespace(namespace: string): Socket | null {
    if (namespace === "/admin") {
      return this.adminSocket;
    }
    if (namespace === "/payments") {
      return this.paymentsSocket;
    }
    if (namespace === "/dashboard") {
      return this.dashboardSocket;
    }
    if (namespace === "/audit") {
      return this.auditSocket;
    }
    return null;
  }

  /**
   * Subscribe to an event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic event subscription
  subscribe<T = any>(event: string, handler: EventHandler<T>): () => void {
    const namespace = getNamespaceForEvent(event);
    const subscription: EventSubscription = { event, handler, namespace };

    const eventKey = `${namespace}:${event}`;
    const existingSubscriptions = this.subscriptions.get(eventKey) || [];
    existingSubscriptions.push(subscription);
    this.subscriptions.set(eventKey, existingSubscriptions);

    // Register handler on socket if connected
    const socket = this.getSocketForNamespace(namespace);
    if (socket && socket.connected) {
      socket.on(event, handler);
    }

    if (import.meta.env.DEV) {
      console.log(
        `[Socket.IO Admin] Subscribed to event: ${event} on ${namespace}`,
      );
    }

    // Return unsubscribe function
    return () => this.unsubscribe(event, handler, namespace);
  }

  /**
   * Unsubscribe from an event
   */
  unsubscribe(event: string, handler: EventHandler, namespace?: string): void {
    const eventNamespace = namespace || getNamespaceForEvent(event);
    const eventKey = `${eventNamespace}:${event}`;

    const eventSubscriptions = this.subscriptions.get(eventKey);
    if (eventSubscriptions) {
      const filtered = eventSubscriptions.filter(
        (sub) => sub.handler !== handler,
      );
      this.subscriptions.set(eventKey, filtered);

      // Remove handler from socket
      const socket = this.getSocketForNamespace(eventNamespace);
      if (socket) {
        socket.off(event, handler);
      }

      if (import.meta.env.DEV) {
        console.log(
          `[Socket.IO Admin] Unsubscribed from event: ${event} on ${eventNamespace}`,
        );
      }
    }
  }

  /**
   * Send message to server (emit event)
   */
  async send(event: string, ...args: unknown[]): Promise<void> {
    const namespace = getNamespaceForEvent(event);
    const socket = this.getSocketForNamespace(namespace);

    if (!socket) {
      console.error(
        `[Socket.IO Admin] Socket not initialized for namespace: ${namespace}`,
      );
      return;
    }

    if (!socket.connected) {
      // Queue message for later
      this.messageQueue.push({ event, data: args, namespace });
      console.log(`[Socket.IO Admin] Message queued: ${event} on ${namespace}`);
      return;
    }

    try {
      socket.emit(event, ...args);
      if (import.meta.env.DEV) {
        console.log(
          `[Socket.IO Admin] Message sent: ${event} on ${namespace}`,
          args,
        );
      }
    } catch (error) {
      console.error("[Socket.IO Admin] Send failed:", error);
      throw error;
    }
  }

  /**
   * Emit event and wait for response (request-response pattern)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic invoke response
  async invoke<T = any>(event: string, ...args: unknown[]): Promise<T> {
    const namespace = getNamespaceForEvent(event);
    const socket = this.getSocketForNamespace(namespace);

    if (!socket) {
      throw new Error(`Socket not initialized for namespace: ${namespace}`);
    }

    if (!socket.connected) {
      throw new Error(`Not connected to ${namespace}`);
    }

    return new Promise<T>((resolve, reject) => {
      socket.emit(event, ...args, (response: T) => {
        if (import.meta.env.DEV) {
          console.log(
            `[Socket.IO Admin] Invoke response: ${event} on ${namespace}`,
            response,
          );
        }
        resolve(response);
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error(`Timeout waiting for response to ${event}`));
      }, 10000);
    });
  }

  /**
   * Flush message queue (send pending messages)
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) {
      return;
    }

    console.log(
      `[Socket.IO Admin] Flushing ${this.messageQueue.length} queued messages`,
    );

    const queue = [...this.messageQueue];
    this.messageQueue = [];

    queue.forEach(({ event, data, namespace }) => {
      const socket = this.getSocketForNamespace(namespace);
      if (socket && socket.connected) {
        socket.emit(event, ...data);
      } else {
        // Re-queue if still not connected
        this.messageQueue.push({ event, data, namespace });
      }
    });
  }

  /**
   * Re-register all subscriptions on reconnection
   */
  private reRegisterSubscriptions(): void {
    this.subscriptions.forEach((subs, eventKey) => {
      // eventKey format: "/admin:notification:received" or "/payments:payment:verified"
      const parts = eventKey.split(":");
      const namespace = parts[0]; // "/admin" or "/payments"
      const event = parts.slice(1).join(":"); // "notification:received" or "payment:verified"
      const socket = this.getSocketForNamespace(namespace);

      if (socket && socket.connected) {
        subs.forEach((sub) => {
          socket.on(event, sub.handler);
        });
      }
    });
  }

  /**
   * Update connection state and notify listeners
   */
  private updateConnectionState(state: ConnectionState): void {
    // Only update if state actually changed
    if (this.connectionState === state) {
      return;
    }

    this.connectionState = state;
    this.stateChangeListeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        console.error(
          "[Socket.IO Admin] Error in state change listener:",
          error,
        );
      }
    });

    // Re-register subscriptions when connected
    if (state === ConnectionState.Connected) {
      this.reRegisterSubscriptions();
    }
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.stateChangeListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.stateChangeListeners.indexOf(listener);
      if (index !== -1) {
        this.stateChangeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === ConnectionState.Connected;
  }
}

/**
 * Singleton instance
 */
export const socketIOManager = new SocketIOConnectionManager();

/**
 * Export default manager
 */
export default socketIOManager;
